import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RoleRoute } from "./components/RoleRoute";

const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Projects = lazy(() => import("./pages/Projects"));
const Staff = lazy(() => import("./pages/Staff"));
const loadTimeEntry = () => import("./pages/TimeEntry");
const TimeEntry = lazy(loadTimeEntry);
const Payroll = lazy(() => import("./pages/Payroll"));
const Invoicing = lazy(() => import("./pages/Invoicing"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const loadSettings = () => import("./pages/Settings");
const Settings = lazy(loadSettings);
const Quoting = lazy(() => import("./pages/Quoting"));
const QuotesOverview = lazy(() => import("./pages/QuotesOverview"));
const loadMaterialLibrary = () => import("./pages/MaterialLibrary");
const MaterialLibrary = lazy(loadMaterialLibrary);
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const MyProjects = lazy(() => import("./pages/MyProjects"));
const MyProjectDetail = lazy(() => import("./pages/MyProjectDetail"));
const LockedWeeksAdmin = lazy(() => import("./pages/LockedWeeksAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HostAdminPage = lazy(() => import("./pages/HostAdmin"));
const DeferredToasterView = lazy(() =>
  import("@/components/ui/toaster").then((module) => ({ default: module.Toaster })),
);

const adminRoles = ["admin"] as const;
const projectManagerRoles = ["admin", "project_manager"] as const;
const siteManagerRoles = ["admin", "project_manager", "site_manager"] as const;
const employeeRoles = ["admin", "project_manager", "site_manager", "employee"] as const;

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-sm font-medium text-muted-foreground">
    Indlaeser...
  </div>
);

function DeferredToaster() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timeoutId: number | undefined;
    const showToaster = () => {
      timeoutId = window.setTimeout(() => setReady(true), 4500);
    };

    if (document.readyState === "complete") {
      showToaster();
    } else {
      window.addEventListener("load", showToaster, { once: true });
    }

    return () => {
      window.removeEventListener("load", showToaster);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  if (!ready) return null;

  return (
    <Suspense fallback={null}>
      <DeferredToasterView />
    </Suspense>
  );
}

const App = () => (
  <>
    <DeferredToaster />
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Login />} />
          <Route path="/dashboard" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><Dashboard /></RoleRoute>} />
          <Route path="/projects" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><Projects /></RoleRoute>} />
          <Route path="/projects/:id" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><ProjectDetail /></RoleRoute>} />
          <Route path="/my-projects" element={<RoleRoute allowedRoles={[...siteManagerRoles]}><MyProjects /></RoleRoute>} />
          <Route path="/my-projects/:id" element={<RoleRoute allowedRoles={[...siteManagerRoles]}><MyProjectDetail /></RoleRoute>} />
          <Route path="/staff" element={<RoleRoute allowedRoles={[...adminRoles]}><Staff /></RoleRoute>} />
          <Route path="/time-entry" element={<RoleRoute allowedRoles={[...employeeRoles]} preload={loadTimeEntry}><TimeEntry /></RoleRoute>} />
          <Route path="/locked-weeks" element={<RoleRoute allowedRoles={[...adminRoles]}><LockedWeeksAdmin /></RoleRoute>} />
          <Route path="/payroll" element={<RoleRoute allowedRoles={[...adminRoles]}><Payroll /></RoleRoute>} />
          <Route path="/invoicing" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><Invoicing /></RoleRoute>} />
          <Route path="/invoices/:id" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><InvoiceDetail /></RoleRoute>} />
          <Route path="/quotes" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><QuotesOverview /></RoleRoute>} />
          <Route path="/quoting" element={<RoleRoute allowedRoles={[...projectManagerRoles]}><Quoting /></RoleRoute>} />
          <Route path="/material-library" element={<RoleRoute allowedRoles={[...projectManagerRoles]} preload={loadMaterialLibrary}><MaterialLibrary /></RoleRoute>} />
          <Route path="/settings" element={<RoleRoute allowedRoles={[...employeeRoles]} preload={loadSettings}><Settings /></RoleRoute>} />
          <Route path="/owner" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="dashboard" /></RoleRoute>} />
          <Route path="/owner/virksomheder" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="companies" /></RoleRoute>} />
          <Route path="/owner/fakturering" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="billing" /></RoleRoute>} />
          <Route path="/owner/brugere" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="users" /></RoleRoute>} />
          <Route path="/owner/adgangsanmodninger" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="requests" /></RoleRoute>} />
          <Route path="/owner/logs" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="logs" /></RoleRoute>} />
          <Route path="/owner/rapporter" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="reports" /></RoleRoute>} />
          <Route path="/owner/indstillinger" element={<RoleRoute allowedRoles={["owner"]}><HostAdminPage view="settings" /></RoleRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </>
);

export default App;
